import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { isInfraAdmin } from "../rbac";
import { runInstanceAction } from "../instanceActions";
import { recoveryOf } from "../InstanceActions/InstanceActionsStore";
import type InstanceActionsStore from "../InstanceActions/InstanceActionsStore";
import type InstanceDetailStore from "../InstanceDetail/InstanceDetailStore";
import type {
  ClientInstance,
  InstanceActionResult,
  InstanceEnvChange,
  InstanceEnvEntry,
  InstanceEnvPreview,
  InstanceEnvRead,
  RecreateRecovery,
} from "../infrastructure.types";

/** The slice of a raw Feathers client service this store calls. */
interface RawService {
  get: (id: string, params?: object) => Promise<unknown>;
  create: (data: object, params?: object) => Promise<unknown>;
}

/** How long a revealed value stays on screen before it is hidden again. */
export const REVEAL_TTL_MS = 60_000;

/** A name HQ will set — the API's `ENV_KEY_RE` (env-rules.ts), checked here for a quick answer only. */
export const ENV_KEY_RE = /^[A-Za-z_][A-Za-z0-9_.-]{0,254}$/;

/** A variable being added on the card (not yet sent anywhere). */
export interface AddedVariable {
  id: number;
  key: string;
  value: string;
}

/** What the last apply did, shown on the card after its dialog closes. */
export interface EnvApplyOutcome {
  ok: boolean;
  message: string;
  /** On failure: what the API says became of the original container. */
  recovery: RecreateRecovery | null;
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * The Environment card on Infrastructure → instance (#3468): an administrator
 * reads a client container's variables, edits, adds and removes them, reviews
 * the change and applies it — HQ recreates the container through the same
 * guarded path an update takes, so the change gets the progress panel, the
 * health wait and the restore of the previous container (and environment)
 * when the new one fails.
 *
 * Every call goes through the RAW Feathers client (`instance-env`), never a
 * ServiceStore: a ServiceStore caches create responses, and a reveal's answer
 * is a secret. The read itself carries no secret — the API masks every
 * secret-looking value — but it is one container's environment, not an HQ
 * entity, so it stays the result of this read and nothing else.
 *
 * WHAT IS KEPT, AND FOR HOW LONG. A revealed value lives in `revealed` for
 * {@link REVEAL_TTL_MS} and is then dropped; a copied one is never kept at
 * all. Values typed into the draft live here until they are applied,
 * discarded, the page moves to another instance, or the person signs out
 * (`reset`). Nothing is written to storage.
 *
 * RBAC: the card is an administrator's. The API decides (admin, live account,
 * no API keys) and audits every reveal and every change; this store only
 * avoids offering what the API would refuse.
 */
class InstanceEnvStore {
  rootStore: RootStore;

  instanceId: string | null = null;
  read: InstanceEnvRead | null = null;
  loading = false;
  loadError: string | null = null;

  // ---- The draft (nothing leaves the browser until Review) ----
  /** key → the new value for a variable the container has. */
  drafts: Record<string, string> = {};
  /** Variables to remove. */
  removed: string[] = [];
  /** Variables to add. */
  added: AddedVariable[] = [];
  nextAddedId = 1;
  /** The row whose value is being edited. */
  editingKey: string | null = null;

  // ---- Reveal / copy (admin, audited by the API) ----
  revealed: Record<string, string> = {};
  revealingKey: string | null = null;
  revealError: string | null = null;
  /** The key whose value was just put on the clipboard (a moment's feedback). */
  copiedKey: string | null = null;

  // ---- Review + apply ----
  preview: InstanceEnvPreview | null = null;
  /** EXACTLY the change the preview was asked about — what the dialog shows,
   *  and the only thing Apply sends (never the live draft). */
  reviewedChange: InstanceEnvChange | null = null;
  previewing = false;
  previewError: string | null = null;
  applying = false;
  applyError: string | null = null;
  outcome: EnvApplyOutcome | null = null;

  /** Bumped by every load; a reply for an older one is dropped. */
  private loadSeq = 0;
  /** Timers that hide revealed values — imperative plumbing, never observed. */
  private revealTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<InstanceEnvStore, "loadSeq" | "revealTimers">(this, {
      rootStore: false,
      loadSeq: false,
      revealTimers: false,
    });
  }

  // ---- Wiring ----

  private get service(): RawService | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawService } }).app;
    return app?.service("instance-env") ?? null;
  }

  private get actionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("instance-actions") ?? this.rootStore.instanceActionsStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  private get actionsStore(): InstanceActionsStore | undefined {
    return this.rootStore.instanceActionsViewStore as InstanceActionsStore | undefined;
  }

  private get detailStore(): InstanceDetailStore | undefined {
    return this.rootStore.instanceDetailStore as InstanceDetailStore | undefined;
  }

  /** The card is an administrator's (the API decides; this only hides it). */
  get isAdmin(): boolean {
    return isInfraAdmin(this.rootStore);
  }

  // ---- Loading ----

  /** Route entry / instance change: read the container's environment (admins only). */
  async load(instanceId: string): Promise<void> {
    if (this.instanceId !== instanceId) this.clearForInstance();
    this.instanceId = instanceId;
    if (!this.isAdmin) return;
    const seq = ++this.loadSeq;
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.loadError = null;
    try {
      const read = (await this.service?.get(instanceId)) as InstanceEnvRead | undefined;
      if (ended() || seq !== this.loadSeq) return;
      runInAction(() => {
        this.read = read ?? null;
        if (!read) this.loadError = "HQ's API did not answer with this container's environment.";
      });
    } catch (err: unknown) {
      if (ended() || seq !== this.loadSeq || isSessionEnded(err)) return;
      runInAction(() => {
        this.read = null;
        this.loadError = messageOf(err, "The environment could not be loaded.");
      });
    } finally {
      if (seq === this.loadSeq && !ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  async reload(): Promise<void> {
    if (this.instanceId) await this.load(this.instanceId);
  }

  // ---- Reading the list ----

  get entries(): InstanceEnvEntry[] {
    return this.read?.entries ?? [];
  }

  /** Why the change cannot be applied right now, or null. */
  get applyBlocker(): string | null {
    const r = this.read;
    if (!r) return null;
    if (r.selfContainer) return "This is the container serving HQ. Change its environment in Portainer.";
    if (r.disabled) return "Actions on this instance are blocked — re-enable it first.";
    if (r.inFlight) return "Another update, move or change is running on this container. Reload when it finishes.";
    return null;
  }

  // ---- The draft ----

  beginEdit(key: string) {
    this.editingKey = key;
  }

  endEdit() {
    this.editingKey = null;
  }

  /** The new value for an existing variable; the same as now means no change. */
  setDraft(key: string, value: string) {
    const entry = this.entries.find((e) => e.key === key);
    if (entry && !entry.masked && entry.value === value) {
      const next = { ...this.drafts };
      delete next[key];
      this.drafts = next;
    } else {
      this.drafts = { ...this.drafts, [key]: value };
    }
    this.dropReview();
  }

  /** Undo a pending change to one existing variable (edit or removal). */
  undo(key: string) {
    const next = { ...this.drafts };
    delete next[key];
    this.drafts = next;
    this.removed = this.removed.filter((k) => k !== key);
    if (this.editingKey === key) this.editingKey = null;
    this.dropReview();
  }

  toggleRemove(key: string) {
    if (this.removed.includes(key)) {
      this.removed = this.removed.filter((k) => k !== key);
    } else {
      this.removed = [...this.removed, key];
      const next = { ...this.drafts };
      delete next[key];
      this.drafts = next;
      if (this.editingKey === key) this.editingKey = null;
    }
    this.dropReview();
  }

  addRow() {
    this.added = [...this.added, { id: this.nextAddedId, key: "", value: "" }];
    this.nextAddedId += 1;
    this.dropReview();
  }

  setAddedKey(id: number, key: string) {
    this.added = this.added.map((a) => (a.id === id ? { ...a, key } : a));
    this.dropReview();
  }

  setAddedValue(id: number, value: string) {
    this.added = this.added.map((a) => (a.id === id ? { ...a, value } : a));
    this.dropReview();
  }

  dropAdded(id: number) {
    this.added = this.added.filter((a) => a.id !== id);
    this.dropReview();
  }

  /** The reviewed change no longer stands (the draft moved, or it was used). */
  private dropReview() {
    this.preview = null;
    this.reviewedChange = null;
  }

  /** Forget every pending change on the card. */
  discard() {
    this.drafts = {};
    this.removed = [];
    this.added = [];
    this.editingKey = null;
    this.dropReview();
    this.previewError = null;
    this.applyError = null;
  }

  /** The change as the API takes it. */
  get change(): InstanceEnvChange {
    const set: InstanceEnvChange["set"] = Object.entries(this.drafts).map(([key, value]) => ({ key, value }));
    for (const a of this.added) if (a.key.trim()) set.push({ key: a.key.trim(), value: a.value });
    return { set, remove: [...this.removed] };
  }

  get changeCount(): number {
    const c = this.change;
    return c.set.length + c.remove.length;
  }

  /** What is wrong with the draft, said before anything is sent. The API checks it all again. */
  get draftProblems(): string[] {
    const out: string[] = [];
    const existing = new Set(this.entries.map((e) => e.key));
    const seen = new Set<string>();
    for (const a of this.added) {
      const key = a.key.trim();
      if (!key) {
        out.push("A new variable has no name.");
        continue;
      }
      if (!ENV_KEY_RE.test(key)) out.push(`"${key}" is not a variable name (letters, digits, _ . or -, not starting with a digit).`);
      else if (existing.has(key)) out.push(`${key} already exists — edit it in the list instead.`);
      else if (seen.has(key)) out.push(`${key} is added twice.`);
      seen.add(key);
    }
    return out;
  }

  get canReview(): boolean {
    return this.isAdmin && !!this.read && !this.applyBlocker && this.changeCount > 0 && this.draftProblems.length === 0;
  }

  // ---- Reveal / copy ----

  /** Show ONE masked value (the API records who, when and which key). */
  async reveal(key: string): Promise<boolean> {
    if (!this.instanceId || !this.isAdmin) return false;
    const instanceId = this.instanceId;
    const ended = sessionMark(this.rootStore);
    this.revealingKey = key;
    this.revealError = null;
    try {
      const answer = (await this.service?.create({ action: "reveal", instanceId, key, purpose: "reveal" })) as
        | { value?: unknown }
        | undefined;
      if (ended() || this.instanceId !== instanceId) return false;
      if (typeof answer?.value !== "string") throw new Error("HQ's API did not answer with the value.");
      const value = answer.value;
      runInAction(() => {
        this.revealed = { ...this.revealed, [key]: value };
      });
      this.scheduleHide(key);
      return true;
    } catch (err: unknown) {
      if (ended() || isSessionEnded(err)) return false;
      runInAction(() => {
        this.revealError = messageOf(err, `${key} could not be revealed.`);
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          if (this.revealingKey === key) this.revealingKey = null;
        });
      }
    }
  }

  /** Hide a revealed value now. */
  hide(key: string) {
    const timer = this.revealTimers[key];
    if (timer) clearTimeout(timer);
    delete this.revealTimers[key];
    const next = { ...this.revealed };
    delete next[key];
    this.revealed = next;
  }

  private scheduleHide(key: string) {
    const existing = this.revealTimers[key];
    if (existing) clearTimeout(existing);
    this.revealTimers[key] = setTimeout(() => this.hide(key), REVEAL_TTL_MS);
  }

  /**
   * Put a value on the clipboard. A masked one is fetched for the copy (the
   * API records it as a copy) and never kept or shown; a plain one is copied
   * from the list.
   */
  async copy(key: string): Promise<boolean> {
    if (!this.instanceId || !this.isAdmin) return false;
    const instanceId = this.instanceId;
    const entry = this.entries.find((e) => e.key === key);
    if (!entry) return false;
    const ended = sessionMark(this.rootStore);
    this.revealError = null;
    try {
      let value: string | null = entry.masked ? null : entry.value;
      if (value === null) {
        this.revealingKey = key;
        const answer = (await this.service?.create({ action: "reveal", instanceId, key, purpose: "copy" })) as
          | { value?: unknown }
          | undefined;
        if (ended() || this.instanceId !== instanceId) return false;
        if (typeof answer?.value !== "string") throw new Error("HQ's API did not answer with the value.");
        value = answer.value;
      }
      await navigator.clipboard.writeText(value);
      if (ended()) return false;
      runInAction(() => {
        this.copiedKey = key;
      });
      return true;
    } catch (err: unknown) {
      if (ended() || isSessionEnded(err)) return false;
      runInAction(() => {
        this.revealError = messageOf(err, `${key} could not be copied.`);
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          if (this.revealingKey === key) this.revealingKey = null;
        });
      }
    }
  }

  // ---- Review + apply ----

  /** Ask the API what the draft would do (validated as the apply will be). */
  async review(): Promise<boolean> {
    const read = this.read;
    if (!this.instanceId || !read || !this.canReview) return false;
    const instanceId = this.instanceId;
    const ended = sessionMark(this.rootStore);
    // The payload as sent: the review stands for this and nothing else.
    const sent = this.change;
    const sentKey = JSON.stringify(sent);
    this.previewing = true;
    this.previewError = null;
    this.dropReview();
    try {
      const preview = (await this.service?.create({
        action: "preview",
        instanceId,
        env: sent,
        envVersion: read.envVersion,
      })) as InstanceEnvPreview | undefined;
      if (ended() || this.instanceId !== instanceId) return false;
      if (!preview || !Array.isArray(preview.diff)) throw new Error("HQ's API did not answer with the change.");
      // Edited while HQ checked it: this answer is about another change.
      if (JSON.stringify(this.change) !== sentKey) {
        runInAction(() => {
          this.previewError = "The change was edited while HQ checked it. Review it again.";
        });
        return false;
      }
      runInAction(() => {
        this.preview = preview;
        this.reviewedChange = sent;
      });
      return true;
    } catch (err: unknown) {
      if (ended() || isSessionEnded(err)) return false;
      runInAction(() => {
        this.previewError = messageOf(err, "The change could not be checked.");
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.previewing = false;
        });
      }
    }
  }

  /**
   * Apply the reviewed change: HQ recreates the container with it (same image,
   * same settings) and waits for its health check, putting the previous
   * container — with the previous environment — back when it fails. Followed
   * by the shared progress panel. Returns true on success; on failure the
   * reason is on `applyError`.
   */
  async apply(): Promise<boolean> {
    const read = this.read;
    const preview = this.preview;
    const change = this.reviewedChange;
    const instance = this.instanceRecord;
    if (!this.instanceId || !read || !preview || !change || !instance || !this.isAdmin || this.applying) return false;
    const actions = this.actionsStore;
    if (!actions) return false;
    const instanceId = this.instanceId;
    const ended = sessionMark(this.rootStore);
    this.applying = true;
    this.applyError = null;
    this.outcome = null;
    try {
      const result = (await actions.runWithProgress(instance, "set-env", (progressToken) =>
        runInstanceAction(this.actionsService, {
          action: "set-env",
          instanceId,
          env: change,
          envVersion: preview.envVersion,
          progressToken,
        })
      )) as InstanceActionResult | undefined;
      if (ended()) return false;
      // The page moved on while it ran: nothing to show it on, nothing to re-read.
      if (this.instanceId !== instanceId) return true;
      runInAction(() => {
        this.outcome = { ok: true, message: describeApplied(result), recovery: null };
        this.discard();
      });
      await this.afterApply(instanceId);
      return true;
    } catch (err: unknown) {
      if (ended() || isSessionEnded(err)) return false;
      if (this.instanceId !== instanceId) return false;
      const message = messageOf(err, "The environment change failed.");
      const recovery = recoveryOf(err);
      runInAction(() => {
        this.applyError = message;
        this.outcome = { ok: false, message, recovery };
        // The draft is kept: the person can correct it and try again.
        this.dropReview();
      });
      await this.afterApply(instanceId);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.applying = false;
        });
      }
    }
  }

  /** Re-read everything the change touched: the variables, the record, its history. */
  private async afterApply(instanceId: string): Promise<void> {
    await Promise.allSettled([
      this.load(instanceId),
      this.detailStore?.reload(),
      this.actionsStore?.auditInstanceId === instanceId ? this.actionsStore?.loadAudit(instanceId) : undefined,
    ]);
  }

  /** The loaded instance record the change is about. */
  private get instanceRecord(): ClientInstance | null {
    const instance = this.detailStore?.instance ?? null;
    return instance && this.instanceId && instance.id.toLowerCase() === this.instanceId.toLowerCase() ? instance : null;
  }

  clearOutcome() {
    this.outcome = null;
  }

  // ---- Lifecycle ----

  /** Another instance: nothing of the previous one's draft or values carries over. */
  private clearForInstance() {
    for (const key of Object.keys(this.revealTimers)) clearTimeout(this.revealTimers[key]);
    this.revealTimers = {};
    this.read = null;
    this.loadError = null;
    this.loading = false;
    this.revealed = {};
    this.revealingKey = null;
    this.revealError = null;
    this.copiedKey = null;
    this.outcome = null;
    this.applying = false;
    this.discard();
  }

  /**
   * The person left the instance console: a revealed value or a typed secret
   * does not stay in memory behind a page nobody is looking at.
   */
  leave() {
    this.loadSeq += 1;
    this.clearForInstance();
    this.instanceId = null;
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out —
   * a revealed value, a typed secret — is left for the next one. Called by
   * RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loadSeq += 1;
    this.clearForInstance();
    this.instanceId = null;
    this.nextAddedId = 1;
    this.previewing = false;
  }
}

/** "Applied: changed PORT; added FEATURE_X — healthy after 34 s." */
export function describeApplied(result: InstanceActionResult | undefined): string {
  const env = result?.env;
  if (!env) return "Applied.";
  const group = (change: string) => env.diff.filter((d) => d.change === change).map((d) => d.key);
  const parts: string[] = [];
  const changed = group("changed");
  const added = group("added");
  const removed = group("removed");
  const reset = group("reset-to-image");
  if (changed.length) parts.push(`changed ${changed.join(", ")}`);
  if (added.length) parts.push(`added ${added.join(", ")}`);
  if (removed.length) parts.push(`removed ${removed.join(", ")}`);
  if (reset.length) parts.push(`${reset.join(", ")} back to the image's value`);
  const secs = Math.round((env.healthWaitedMs ?? 0) / 1000);
  const how =
    env.outcome === "healthy"
      ? `the new container is healthy (after ${secs} s)`
      : env.outcome === "not-started"
        ? "the container was stopped and stays stopped — the change takes effect when it is started"
        : `the new container is running after ${secs} s; it has no health check, so HQ cannot say it is healthy`;
  const partial = result?.rowUpdated === false ? " HQ's instance record was not updated; it catches up on the next discovery." : "";
  return `Applied: ${parts.join("; ") || "the environment"} — ${how}.${partial}`;
}

export default InstanceEnvStore;
